import React, { FunctionComponent, HTMLAttributes, MouseEventHandler, useState } from "react";

import { bemClassNames } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { ButtonBackgroundColor } from "../ButtonProvider";
import { Icon, IconColor } from "../Icon";
import { IconButton } from "../IconButton";
import { Text, WhiteSpace } from "../Text";
import { DragOverPosition } from "./types";

type DefaultItemRendererProps = {
    readonly htmlElementProps: HTMLAttributes<HTMLElement>;
    readonly itemId: string;
    readonly value: string;
    readonly isExpanded: boolean;
    readonly hasChildren: boolean;
    readonly dragOverPosition: DragOverPosition | null;
    readonly onChevronClick: (itemId: string) => void;
    readonly onItemClick: (itemId: string) => void;
};

const classNames = bemClassNames("default-item-renderer");

/** Defaultní implementace pro renderer řádku komponenty DraggableTree. Tuto komponentu můžete, ale nemusíte využít pro zobrazení řádků vašeho stromu. */
export const DefaultItemRenderer: FunctionComponent<DefaultItemRendererProps> = ({ htmlElementProps, itemId, value, isExpanded, hasChildren, dragOverPosition, onChevronClick, onItemClick }) => {
    const [isMouseOver, setIsMouseOver] = useState(false);

    const handleItemClick = () => onItemClick(itemId);
    const enableHover = () => setIsMouseOver(true);
    const disableHover = () => setIsMouseOver(false);
    const handleChevronClick: MouseEventHandler<HTMLSpanElement> = (e) => {
        e.stopPropagation();
        onChevronClick(itemId);
    };

    return (
        <div className={classNames({ "mouse-over": isMouseOver })} onBlur={disableHover} onFocus={enableHover} onMouseOver={enableHover} onMouseLeave={disableHover} {...htmlElementProps}>
            <span className={classNames("inner-wraper", { "drag-over": dragOverPosition !== null })} onClick={handleItemClick}>
                {hasChildren && (
                    <IconButton
                        iconCode={isExpanded ? IconCode.uiChevronBottomSmall : IconCode.uiChevronRightSmall}
                        color={IconColor.colorPrimary}
                        onClick={handleChevronClick}
                        backgroundColor={ButtonBackgroundColor.transparent} />
                )}
                <span className={classNames("value-wrapper", { "has-children": hasChildren })}>
                    <Icon iconCode={IconCode.uiCardView} color={IconColor.blue} />
                    <Text value={value} isTextSelectable={false} ellipsis whiteSpace={WhiteSpace.nowrap} />
                </span>
            </span>
        </div>
    );
};
